import React, { useState, useRef, useEffect } from 'react';
import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';

// ═══════════════════════════════════════════════════════════
//  KESIMPULAN AI — makmal /pipeline-test, tab "Ujian Master Prompt"
//  (2026-10-06, keputusan user)
//
//  Butang "Minta AI simpulkan" → Cloud Function `pipelineTestKesimpulan`
//  (admin sahaja; kunci OpenAI kekal di server). AI yang lebih kuat membaca
//  kedua-dua kolum dan menulis keputusan pendek.
//
//  Atas permintaan (butang), BUKAN automatik — setiap klik ada kos.
//
//  ⚠️ KEMBAR: bentuk payload = `semakInput` dalam functions/pipelineTestKesimpulan.js.
//     Medan disenarai SATU PERSATU di `binaPayload` — medan yang tidak disebut
//     di sini tidak akan sampai ke server.
// ═══════════════════════════════════════════════════════════

const usd = (x) => '$' + Number(x || 0).toFixed(4);

const LABEL_KEPUTUSAN = {
  kanan_lebih_baik: { teks: 'Kanan lebih baik', kelas: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  setara: { teks: 'Setara', kelas: 'bg-slate-100 text-slate-700 border-slate-200' },
  kiri_lebih_baik: { teks: 'Kiri (production) lebih baik', kelas: 'bg-amber-50 text-amber-800 border-amber-200' },
};
const LABEL_MENANG = {
  kiri: { teks: 'kiri', kelas: 'bg-amber-50 text-amber-700' },
  kanan: { teks: 'kanan', kelas: 'bg-emerald-50 text-emerald-700' },
  seri: { teks: 'seri', kelas: 'bg-slate-100 text-slate-500' },
};

const adaHasil = (col) => Array.isArray(col?.creative?.sets) && col.creative.sets.length > 0;

function ringkasSet(s) {
  return {
    hookText: s?.hookText || '',
    promptImage: s?.promptImage || '',
    promptVideo: s?.promptVideo || '',
    videoScript: s?.videoScript || '',
    captionTiktok: s?.captionTiktok || '',
    scenes: Array.isArray(s?.scenes)
      ? s.scenes.map((b) => ({ scene: b?.scene ?? null, videoScript: b?.videoScript || '', promptVideo: b?.promptVideo || '' }))
      : [],
  };
}

function ringkasKolum(col) {
  return {
    sets: (col?.creative?.sets || []).map(ringkasSet),
    model: col?.creative?.model || '',
    reasoning: col?.creative?.reasoning || '',
    kosUSD: Number.isFinite(col?.totalCostUSD) ? col.totalCostUSD : null,
    token: Number.isFinite(col?.writerOutputTokens) ? col.writerOutputTokens : null,
  };
}

function binaPayload({ result, promptSemasa, promptBaru, dataProduk, namaModule }) {
  return {
    namaProduk: result?.meta?.productName || '',
    namaModule: namaModule || '',
    promptSemasa: promptSemasa || '',
    promptBaru: promptBaru || '',
    kiri: ringkasKolum(result?.current),
    kanan: ringkasKolum(result?.optimized),
    dataProduk: dataProduk || '',
  };
}

/** Sebab butang tidak boleh ditekan — null bila semuanya sedia. */
function sebabTakBoleh({ result, promptSemasa, promptBaru }) {
  if (!result?.meta?.dataSama) {
    return 'Run ini dibuat dengan cara lama (kolum kiri bukan setting production, data tidak sama). Tekan "Run ujian" semula untuk dapatkan kesimpulan yang adil.';
  }
  if (!adaHasil(result?.current)) return 'Kolum kiri tiada hasil (gagal atau output terpotong). Run semula dahulu.';
  if (!adaHasil(result?.optimized)) return 'Kolum kanan tiada hasil (gagal atau output terpotong). Betulkan setting kolum kanan dan run semula dahulu.';
  if (!String(promptSemasa || '').trim()) return 'Master prompt semasa untuk run ini tidak dijumpai. Pilih module yang sama, kemudian run semula.';
  if (!String(promptBaru || '').trim()) return 'Master prompt baru untuk run ini tidak dijumpai. Run semula dahulu.';
  return null;
}

export default function KesimpulanAiMakmal({ result, promptSemasa, promptBaru, dataProduk, namaModule, onSiap }) {
  const [sedang, setSedang] = useState(false);
  const [ralat, setRalat] = useState(null);
  const hidup = useRef(true);
  useEffect(() => { hidup.current = true; return () => { hidup.current = false; }; }, []);

  const simpanan = result?.kesimpulanAi || null; // { kesimpulan, bezaKosPct, faktaDisemak, hakim }
  const k = simpanan?.kesimpulan || null;
  const halangan = sebabTakBoleh({ result, promptSemasa, promptBaru });

  const minta = async () => {
    if (sedang || halangan) return;
    setSedang(true);
    setRalat(null);
    try {
      const call = httpsCallable(functions, 'pipelineTestKesimpulan', { timeout: 200000 });
      const res = await call(binaPayload({ result, promptSemasa, promptBaru, dataProduk, namaModule }));
      const data = res?.data;
      if (!data?.kesimpulan?.keputusan || !data?.kesimpulan?.ringkasan) {
        throw new Error('Server memulangkan kesimpulan kosong.');
      }
      // Serah kepada induk walaupun komponen ini sudah ditutup — kesimpulan sudah dibayar,
      // jadi ia mesti disimpan pada run asalnya.
      onSiap?.(result, data);
    } catch (e) {
      console.error('[KesimpulanAi] gagal:', e);
      if (hidup.current) setRalat(e?.message || String(e));
    } finally {
      if (hidup.current) setSedang(false);
    }
  };

  const label = k ? (LABEL_KEPUTUSAN[k.keputusan] || LABEL_KEPUTUSAN.setara) : null;
  const pct = simpanan?.bezaKosPct;

  return (
    <div className="mt-3 rounded-xl bg-white border border-slate-200 p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-[18px] text-indigo-600">gavel</span>
          <p className="text-[15px] font-semibold text-slate-800 m-0">Kesimpulan AI</p>
        </div>
        <button
          onClick={minta}
          disabled={sedang || !!halangan}
          className="h-9 flex items-center gap-2 px-4 text-[13px] font-bold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <span className={`material-symbols-outlined text-[16px] ${sedang ? 'animate-spin' : ''}`}>{sedang ? 'progressive_activity' : 'auto_awesome'}</span>
          {sedang ? 'AI sedang membaca…' : (k ? 'Simpulkan semula' : 'Minta AI simpulkan')}
        </button>
      </div>

      {!k && !sedang && !ralat && (
        <p className={`text-[12.5px] m-0 mt-2 ${halangan ? 'text-amber-700' : 'text-slate-500'}`}>
          {halangan || 'AI yang lebih kuat membaca kedua-dua kolum dan beritahu mana lebih bagus. Anggaran 10–20 sen sekali klik; kos sebenar dipaparkan selepas siap.'}
        </p>
      )}

      {sedang && (
        <p className="text-[12.5px] text-slate-500 m-0 mt-2">Biasanya 30 saat – 2 minit. Jangan tutup page ini.</p>
      )}

      {ralat && (
        <div className="mt-3 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2.5 text-[12.5px] text-rose-700">
          <span className="font-bold">Kesimpulan gagal:</span> {ralat}
        </div>
      )}

      {k && (
        <div className="mt-3">
          <div className="flex items-start gap-2.5 flex-wrap">
            <span className={`text-[12px] font-bold px-2.5 py-1 rounded-lg border whitespace-nowrap ${label.kelas}`}>{label.teks}</span>
            <p className="text-[14px] text-slate-800 leading-relaxed m-0 flex-1 min-w-[240px]">{k.ringkasan}</p>
          </div>

          <p className="text-[12.5px] text-slate-600 m-0 mt-3">
            <span className="text-slate-400">Kos satu generate:</span> kiri <b>{usd(result?.current?.totalCostUSD)}</b> · kanan <b>{usd(result?.optimized?.totalCostUSD)}</b>
            {pct != null && <> · <b className={pct < 0 ? 'text-emerald-600' : pct > 0 ? 'text-rose-600' : 'text-slate-600'}>{pct > 0 ? '+' : ''}{pct}%</b> ({pct < 0 ? 'kanan lebih murah' : pct > 0 ? 'kanan lebih mahal' : 'sama'})</>}
          </p>

          {k.beza?.length > 0 && (
            <div className="mt-3">
              <p className="text-[12px] font-semibold text-slate-500 uppercase tracking-wide m-0 mb-1.5">Beza utama</p>
              <div className="space-y-1.5">
                {k.beza.map((b, i) => {
                  const m = LABEL_MENANG[b.menang] || LABEL_MENANG.seri;
                  return (
                    <div key={i} className="flex items-start gap-2 text-[13px] text-slate-700 leading-relaxed">
                      <span className={`mt-0.5 text-[10.5px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap ${m.kelas}`}>{m.teks}</span>
                      <p className="m-0"><span className="font-semibold text-slate-800">{b.aspek}</span> — {b.catatan}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {k.amaran?.length > 0 && (
            <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5">
              <p className="text-[12px] font-bold text-amber-800 m-0 mb-1">Perlu tahu sebelum tukar</p>
              <ul className="m-0 pl-4 text-[12.5px] text-amber-800 leading-relaxed list-disc">
                {k.amaran.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          )}

          {k.cadangan && (
            <p className="text-[13.5px] text-slate-800 leading-relaxed m-0 mt-3"><span className="font-semibold">Cadangan:</span> {k.cadangan}</p>
          )}

          <p className="text-[11.5px] text-slate-400 m-0 mt-3">
            Pendapat AI ({simpanan?.hakim?.model || '—'}) berdasarkan 1 run sahaja — hasil AI berubah sedikit setiap run, jadi uji beberapa produk sebelum memutuskan.
            {' '}{simpanan?.faktaDisemak ? 'Dakwaan produk disemak lawan data produk.' : 'Ketepatan fakta produk TIDAK disemak (data produk run ini tiada di page).'}
            {' '}Kos kesimpulan ini: {usd(simpanan?.hakim?.kosUSD)}.
          </p>
        </div>
      )}
    </div>
  );
}
